import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { Text } from '@nula/core-ui';
import type { UIMessage, UIMessagePart, UIDataTypes, UITools } from 'ai';
import styles from './status-line.module.css';

type Part = UIMessagePart<UIDataTypes, UITools>;

const root = cva(styles.root);

const DOT_VARIANTS = { bounce: { y: [0, -3, 0] }, still: { y: 0 } };
const DOT_TRANSITION = (delay: number) => ({
  duration: 0.6,
  repeat: Infinity,
  ease: 'easeInOut' as const,
  delay,
});

function StatusDots() {
  return (
    <span className={styles.dots} aria-hidden>
      {[0, 0.15, 0.3].map((delay, i) => (
        <motion.span
          key={i}
          className={styles.dot}
          animate={DOT_VARIANTS.bounce}
          transition={DOT_TRANSITION(delay)}
        />
      ))}
    </span>
  );
}

function isStatusPart(p: Part): p is Part & { type: 'data-status'; data: { text?: string } } {
  return p.type === 'data-status';
}

function isTextPart(p: Part): p is Extract<Part, { type: 'text' }> {
  return p.type === 'text';
}

interface StatusLineProps {
  message: UIMessage;
  className?: string;
}

/**
 * Renders a single always-visible status line for the current assistant
 * turn, fed by the latest non-empty `data-status` UIMessagePart. Replaces
 * the prior placeholder thoughts and tool activity lines with one calm
 * indicator that updates across the whole turn.
 *
 * Visibility rules:
 *   - Hidden when no data-status part is present.
 *   - Hidden once a text part with state === 'done' lands (final answer
 *     has streamed; status is no longer load-bearing).
 *   - Stays visible while text is mid-stream (state !== 'done').
 *   - Empty-text status events are ignored — the prior status remains.
 */
export function StatusLine({ message, className }: StatusLineProps) {
  const parts = message.parts ?? [];

  // Once the final answer has fully streamed, the status line is hidden —
  // the user can see the response, no need for an activity hint.
  const hasDoneTextPart = parts.some(
    (p) => isTextPart(p) && (p as { state?: string }).state === 'done',
  );
  if (hasDoneTextPart) return null;

  // Latest non-empty status wins. We walk forward and take the last
  // non-empty text we see; empty-text status events are skipped so a
  // mistakenly-empty payload doesn't clear the prior text.
  let latestText = '';
  for (const p of parts) {
    if (!isStatusPart(p)) continue;
    const text = p.data?.text ?? '';
    if (text) latestText = text;
  }
  if (!latestText) return null;

  return (
    <div className={clsx(root(), className)}>
      <Text className={styles.text}>{latestText}</Text>
      <StatusDots />
    </div>
  );
}
